import Dataloader from 'dataloader'
import { DistributedOmit } from 'type-fest'
import MeetingTypes, {
  MeetingType,
  Reminder,
  NewReminder,
  SimpleMetadata,
  MeetingTypeDraft,
  NewMeetingTypeWithDefaultsDiscriminator,
  MeetingTypeUpdate,
  GuestFormField,
  MeetingLocation,
  TypeOfMeetingType,
  MeetingTypeWithReminders,
  MeetingTypePersonal,
  PublicMeetingType,
  GuestFormDataFields,
  MeetingTypeWithRemindersTeam,
  GuestFormMixed,
} from '@chilipiper/api-client/src/meeting-types'
import { useWorkspaceId } from '@chilipiper/workspace'
import {
  useQueryClient,
  useMutation,
  useQuery,
  UseQueryOptions,
  QueryClient,
  useQueries,
  queryOptions,
} from '@tanstack/react-query'
import { useApiClient } from '@chilipiper/api-client/src/context'
import { useFireSessionContext } from '@chilipiper/fire-session'
import * as R from 'remeda'
import { useMemo } from 'react'
import invariant from 'tiny-invariant'
import { isEqualWithDateComparison } from './utils'
import { useGetZoomConnectionStatus } from './zoom'
import { usePersonalWorkspace } from './workspace'
import { useDefaultSchedulingLinkId } from './team-scheduling'

export const DEFAULT_MEETING_TYPE_DURATION = '1800000 milliseconds'

type Key = { api: MeetingTypes; id: string; tenantId: string }
const meetingTypesLoader = new Dataloader<Key, PublicMeetingType>(
  async keys => {
    const { tenantId, api } = keys[0]
    const map = await api.publicFindMeetingTypes(tenantId, {
      id: keys.map(key => key.id),
    })
    return keys.map(key => map[key.id] || null)
  },
  {
    cache: false,
  }
)

const meetingTypesQueryKey = 'meeting-types'
const personalMeetingTypesQueryKey = 'personal-meeting-types'
const meetingTypeQueryKey = 'meeting-type'
const defaultMeetingTypeQueryKey = 'default-meeting-type'
const remindersQueryKey = 'reminders'
const personalRemindersQueryKey = 'personal-reminders'
const workspaceRemindersQueryKey = 'workspace-reminders'
const reminderTemplatesQueryKey = 'reminder-templates'
const reminderQueryKey = 'reminder'
export const queryKeyFactory = {
  meetingTypes: (workspaceId: string, name = '', userId = '') => [
    meetingTypesQueryKey,
    'published',
    'workspace',
    workspaceId,
    'name',
    name,
    'userId',
    userId,
  ],
  meetingTypesDrafts: (workspaceId: string, name = '') => [
    meetingTypesQueryKey,
    'draft',
    'workspace',
    workspaceId,
    'name',
    name,
  ],
  personalMeetingTypes: (userId: string) => [personalMeetingTypesQueryKey, 'userId', userId],
  meetingType: (tenantId: string, id: string) => [meetingTypeQueryKey, tenantId, 'published', id],
  meetingTypeDraft: (id: string) => [meetingTypeQueryKey, 'draft', id],
  batchMeetingType: (ids: string[]) => [meetingTypeQueryKey, 'published', 'ids', ids.join(',')],
  meetingTypeByReminder: (reminderId: string) => [
    meetingTypeQueryKey,
    'published',
    'reminder',
    reminderId,
  ],
  reminders: () => [remindersQueryKey],
  reminder: (reminderId: string) => [reminderQueryKey, reminderId],
  personalReminders: () => [personalRemindersQueryKey],
  workspaceReminders: (workspaceId: string) => [workspaceRemindersQueryKey, workspaceId],
  reminderTemplatesQueryKey: (typeOfMeetingType: string) => [
    reminderTemplatesQueryKey,
    typeOfMeetingType,
  ],
  defaultMeetingType: (id?: string) => [defaultMeetingTypeQueryKey, id],
}

export const isTeamMeetingTypeDraft = (
  draft: TypedMeetingTypeDraft['draft']
): draft is MeetingTypeWithRemindersTeam => {
  return draft.type === 'Team'
}

export interface TypedMeetingTypeDraft extends Omit<MeetingTypeDraft, 'draft'> {
  draft: DistributedOmit<MeetingTypeWithReminders, 'metadata' | 'workspaceId' | 'tenantId' | 'slug'>
}

// todo: change revision
export const useUpdateMeetingTypeDraft = (meetingTypeId: string, revision = 0) => {
  const queryClient = useQueryClient()
  const workspaceId = useWorkspaceId()
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return useMutation({
    mutationFn: (meetingTypeDraftUpdate: TypedMeetingTypeDraft['draft']) =>
      meetingTypes.upsertMeetingTypeDraft(
        tenantId,
        workspaceId,
        meetingTypeId,
        { revision: revision },
        meetingTypeDraftUpdate
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.meetingTypesDrafts(workspaceId) })
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.meetingTypeDraft(meetingTypeId) })
    },
    onMutate: async (updates: TypedMeetingTypeDraft['draft']) => {
      await queryClient.cancelQueries({
        queryKey: queryKeyFactory.meetingTypeDraft(meetingTypeId),
      })
      const previousMeetingType = queryClient.getQueryData<TypedMeetingTypeDraft>(
        queryKeyFactory.meetingTypeDraft(meetingTypeId)
      )
      if (previousMeetingType) {
        const updatedMeetingType: TypedMeetingTypeDraft = {
          ...previousMeetingType,
          draft: {
            ...previousMeetingType?.draft,
            ...updates,
          },
        }
        queryClient.setQueryData(
          queryKeyFactory.meetingTypeDraft(meetingTypeId),
          updatedMeetingType
        )

        return { previousMeetingType, updatedMeetingType }
      }
    },
    onError: (_err, _updates, context) => {
      queryClient.setQueryData(
        queryKeyFactory.meetingTypeDraft(meetingTypeId),
        context?.previousMeetingType
      )
    },
    onSuccess: data => {
      updateMeetingTypeDraftCacheWithMetadata(tenantId, meetingTypeId, data.metadata, queryClient)
    },
  })
}

export const useUpdateMeetingType = (meetingTypeId: string) => {
  const queryClient = useQueryClient()
  const { meetingTypesV2 } = useApiClient()
  const { tenantId } = useFireSessionContext()
  const workspaceId = useWorkspaceId()

  const onMutate = async (updates: MeetingTypeUpdate) => {
    await queryClient.cancelQueries({
      queryKey: queryKeyFactory.meetingType(tenantId, meetingTypeId),
    })
    const previousMeetingType = queryClient.getQueryData<MeetingTypeUpdate>(
      queryKeyFactory.meetingType(tenantId, meetingTypeId)
    )
    if (previousMeetingType) {
      const updatedMeetingType: MeetingTypeUpdate = {
        ...previousMeetingType,
        ...updates,
      }
      queryClient.setQueryData(queryKeyFactory.meetingType(tenantId, meetingTypeId), {
        ...updatedMeetingType,
        reminders: previousMeetingType.reminders,
      })

      return { previousMeetingType, updatedMeetingType }
    }
  }

  const mutation = useMutation({
    mutationFn: (meetingTypeUpdate: MeetingTypeUpdate) =>
      // todo: should we have updateMeetingType v2 available which does not use MeetingTypeUpdateDto?
      meetingTypesV2.updateMeetingType(tenantId, workspaceId, meetingTypeId, {
        ...meetingTypeUpdate,
        reminders: meetingTypeUpdate.reminders,
        revision: meetingTypeUpdate.revision,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.meetingTypes(workspaceId) })

      // we want to refetch here instead of simply invalidating as the update expects a MeetingTypeUpdate
      // which accepts reminder ids instead of complete reminder data where on the other hand retrieving meeting type
      // exposes full reminder data which we actually want in the cached meeting type
      queryClient.refetchQueries({ queryKey: queryKeyFactory.meetingType(tenantId, meetingTypeId) })
    },
    onMutate,
    onError: (_err, _updates, context) => {
      queryClient.setQueryData(
        queryKeyFactory.meetingType(tenantId, meetingTypeId),
        context?.previousMeetingType
      )
    },
    onSuccess: data => {
      updateMeetingTypeCacheWithMetadata(tenantId, meetingTypeId, data.metadata, queryClient)
    },
  })

  return { ...mutation, mutateWithoutRequest: onMutate }
}

const updateMeetingTypeCacheWithMetadata = (
  tenantId: string,
  meetingTypeId: string,
  metadata: SimpleMetadata,
  queryClient: QueryClient
) => {
  const meetingType = queryClient.getQueryData<MeetingTypeWithReminders>(
    queryKeyFactory.meetingType(tenantId, meetingTypeId)
  )

  if (!meetingType) {
    return null
  }
  const updatedMeetingType = structuredClone(meetingType)
  updatedMeetingType.metadata = metadata
  queryClient.setQueryData(queryKeyFactory.meetingTypeDraft(meetingTypeId), updatedMeetingType)
}

const updateMeetingTypeDraftCacheWithMetadata = (
  tenantId: string,
  meetingTypeId: string,
  metadata: SimpleMetadata,
  queryClient: QueryClient
) => {
  const meetingType = queryClient.getQueryData<MeetingTypeWithReminders>(
    queryKeyFactory.meetingType(tenantId, meetingTypeId)
  )

  if (!meetingType) {
    return null
  }
  const updatedMeetingType = structuredClone(meetingType)
  updatedMeetingType.metadata = metadata
  queryClient.setQueryData(queryKeyFactory.meetingType(tenantId, meetingTypeId), updatedMeetingType)
}

// todo: remove guest form type since team meeting types can only be data fields
export const useCreateMeetingTypeDraft = (type: NewMeetingTypeWithDefaultsDiscriminator) => {
  const queryClient = useQueryClient()
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()
  const workspaceId = useWorkspaceId()

  return useMutation({
    mutationFn: (name: string) =>
      meetingTypes.createMeetingTypeDraft(tenantId, workspaceId, {
        type,
        name,
        duration: DEFAULT_MEETING_TYPE_DURATION,
      }),
    onSuccess: newMeetingType => {
      queryClient.setQueryData<MeetingTypeDraft[]>(
        queryKeyFactory.meetingTypesDrafts(workspaceId),
        old => {
          const data = old ?? ([] as MeetingTypeDraft[])
          data.push(newMeetingType)
          return data
        }
      )
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.meetingTypesDrafts(workspaceId) }),
  })
}

export const useCreateMeetingType = (
  type: NewMeetingTypeWithDefaultsDiscriminator,
  id?: string
) => {
  const queryClient = useQueryClient()
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()
  const workspaceId = useWorkspaceId()
  const { data: connectionStatus } = useGetZoomConnectionStatus()

  const zoomConnection =
    connectionStatus === 'Connected'
      ? ({
          index: 0,
          type: 'ZoomOneTimeLink',
        } as MeetingLocation)
      : undefined

  return useMutation({
    mutationFn: (name: string, defaultLocation = zoomConnection) =>
      meetingTypes.createMeetingType(tenantId, workspaceId, {
        type,
        name,
        id,
        duration: DEFAULT_MEETING_TYPE_DURATION,
        defaultLocation,
      }),
    onSuccess: newMeetingType => {
      queryClient.setQueryData<MeetingTypeWithReminders[]>(
        queryKeyFactory.meetingTypes(workspaceId),
        old => {
          const data = old ?? ([] as MeetingTypeWithReminders[])
          data.push(newMeetingType)
          return data
        }
      )
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.meetingTypes(workspaceId) }),
  })
}

export const useDuplicateMeetingType = () => {
  const queryClient = useQueryClient()
  const workspaceId = useWorkspaceId()
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return useMutation({
    mutationFn: ({
      duplicateName,
      meetingTypeId,
    }: {
      duplicateName: string
      meetingTypeId: string
    }) =>
      meetingTypes.duplicateMeetingType(tenantId, workspaceId, meetingTypeId, { duplicateName }),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.meetingTypes(workspaceId) }),
  })
}

export const useDeleteMeetingTypeDraft = (workspaceId: string) => {
  const queryClient = useQueryClient()
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return useMutation({
    mutationFn: async (meetingTypeId: string) => {
      return meetingTypes.deleteMeetingTypeDraft(tenantId, workspaceId, meetingTypeId).catch(e => {
        if (e.status === 404) {
          return
        }

        throw e
      })
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeyFactory.meetingTypesDrafts(workspaceId),
      })
    },
  })
}

export const useDeleteMeetingType = (workspaceId: string) => {
  const queryClient = useQueryClient()
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return useMutation({
    mutationFn: (meetingTypeId: string) => {
      return Promise.all([
        meetingTypes.deleteMeetingTypeDraft(tenantId, workspaceId, meetingTypeId).catch(e => {
          if (e.status === 404) {
            return
          }

          throw e
        }),
        meetingTypes.deleteMeetingType(tenantId, workspaceId, meetingTypeId).catch(e => {
          if (e.status === 404) {
            return
          }

          throw e
        }),
      ])
    },
    onMutate: removedMeetingTypeId => {
      const previousDraftItems = queryClient.getQueryData(
        queryKeyFactory.meetingTypesDrafts(workspaceId)
      )
      queryClient.setQueryData<MeetingTypeDraft[]>(
        queryKeyFactory.meetingTypesDrafts(workspaceId),
        old => old?.filter(item => item.draftId !== removedMeetingTypeId)
      )

      const previousItems = queryClient.getQueryData(queryKeyFactory.meetingTypes(workspaceId))
      queryClient.setQueryData<MeetingType[]>(queryKeyFactory.meetingTypes(workspaceId), old =>
        old?.filter(item => item.id !== removedMeetingTypeId)
      )

      return () => {
        queryClient.setQueryData(queryKeyFactory.meetingTypes(workspaceId), previousItems)
        queryClient.setQueryData(
          queryKeyFactory.meetingTypesDrafts(workspaceId),
          previousDraftItems
        )
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.meetingTypesDrafts(workspaceId) })
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.meetingTypes(workspaceId) })
    },
  })
}

export const useMeetingTypeHeader = (
  tenantId = '',
  id = '',
  options?: Omit<
    UseQueryOptions<PublicMeetingType, unknown, PublicMeetingType, string[]>,
    'queryKey' | 'queryFn'
  >
) => {
  const { meetingTypes } = useApiClient()
  return useQuery({
    queryKey: queryKeyFactory.meetingType(tenantId, id),
    queryFn: () => {
      return meetingTypesLoader.load({ tenantId, id, api: meetingTypes })
    },
    ...options,
  })
}

export const useMeetingTypesHeaders = (
  ids: string[] = [],
  options?: Pick<
    UseQueryOptions<PublicMeetingType[], unknown, PublicMeetingType[], string[]>,
    'enabled'
  >
) => {
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()
  return useQuery({
    queryKey: queryKeyFactory.batchMeetingType(ids),
    queryFn: () =>
      Promise.all(ids.map(id => meetingTypesLoader.load({ tenantId, id, api: meetingTypes }))),
    ...options,
  })
}

export const useCreateReminder = (meetingTypeId: string) => {
  const queryClient = useQueryClient()
  const workspaceId = useWorkspaceId()
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return useMutation({
    mutationFn: (newReminder: NewReminder) =>
      meetingTypes.createReminder(tenantId, workspaceId, newReminder),
    onSettled: async () =>
      await queryClient.invalidateQueries({
        queryKey: queryKeyFactory.meetingType(tenantId, meetingTypeId),
      }),
  })
}

export const useUpdateReminder = (reminderId: string, meetingTypeId: string) => {
  const queryClient = useQueryClient()
  const workspaceId = useWorkspaceId()
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return useMutation({
    mutationFn: (reminder: Reminder) => {
      return meetingTypes.updateReminder(tenantId, workspaceId, reminder)
    },
    onMutate: async (updates: Reminder) => {
      await queryClient.cancelQueries({ queryKey: queryKeyFactory.reminder(reminderId) })
      const previousReminder = queryClient.getQueryData<Reminder>(
        queryKeyFactory.reminder(reminderId)
      )
      const updatedReminder = { ...previousReminder, ...updates }

      queryClient.setQueryData(queryKeyFactory.reminder(reminderId), updatedReminder)

      return { previousReminder, updatedReminder }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeyFactory.meetingType(tenantId, meetingTypeId),
      })
      await queryClient.invalidateQueries({ queryKey: queryKeyFactory.reminder(reminderId) })
      await queryClient.invalidateQueries({ queryKey: queryKeyFactory.personalReminders() })
    },
  })
}

export const useMeetingTypesForReminder = (reminderId: string) => {
  const workspaceId = useWorkspaceId()
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return useQuery({
    queryKey: queryKeyFactory.meetingTypeByReminder(reminderId),
    queryFn: () =>
      meetingTypes.findMeetingTypes(tenantId, workspaceId, { 'reminder-id': reminderId }),
    enabled: !!reminderId,
  })
}

export const useReminder = (reminderId: string) => {
  const workspaceId = useWorkspaceId()
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return useQuery({
    queryKey: queryKeyFactory.reminder(reminderId),
    queryFn: () => meetingTypes.findReminder(tenantId, workspaceId, reminderId),
    enabled: !!reminderId,
  })
}

export const useReminderTemplates = (typeOfMeetingType: TypeOfMeetingType) => {
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return useQuery({
    queryKey: queryKeyFactory.reminderTemplatesQueryKey(typeOfMeetingType),
    queryFn: () =>
      meetingTypes
        .findRemindersTemplates(tenantId, { typeOfMeetingType })
        .then(templates => templates.filter(template => template.trigger.type !== 'MeetingBooked')),
  })
}

export const useReminders = () => {
  const workspaceId = useWorkspaceId()
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return useQuery({
    queryKey: queryKeyFactory.reminders(),
    queryFn: () => meetingTypes.findReminders(tenantId, workspaceId),
  })
}

export const useWorkspaceReminders = (workspaceId?: string) => {
  const { meetingTypes } = useApiClient()
  const userWorkspaceId = useWorkspaceId()
  const { tenantId } = useFireSessionContext()
  return useQuery({
    queryKey: queryKeyFactory.workspaceReminders(workspaceId ?? userWorkspaceId),
    queryFn: () => meetingTypes.findReminders(tenantId, workspaceId ?? userWorkspaceId),
  })
}

export const useDeleteReminder = (meetingTypesData?: MeetingType[]) => {
  const queryClient = useQueryClient()
  const workspaceId = useWorkspaceId()
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return useMutation({
    mutationFn: (reminderId: string) =>
      meetingTypes.deleteReminder(tenantId, workspaceId, reminderId),
    onMutate: removedReminderId => {
      const previousWorkspaceReminders = queryClient.getQueryData(queryKeyFactory.reminders())
      const previousPersonalReminders = queryClient.getQueryData(
        queryKeyFactory.personalReminders()
      )

      queryClient.setQueryData<Reminder[]>(queryKeyFactory.reminders(), old =>
        old?.filter(item => item.id !== removedReminderId)
      )
      queryClient.setQueryData<Reminder[]>(queryKeyFactory.personalReminders(), old =>
        old?.filter(item => item.id !== removedReminderId)
      )

      return () => {
        queryClient.setQueryData(queryKeyFactory.reminders(), previousWorkspaceReminders)
        queryClient.setQueryData(queryKeyFactory.personalReminders(), previousPersonalReminders)
      }
    },
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.reminders() })
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.personalReminders() })

      if (meetingTypesData && meetingTypesData.length > 0) {
        meetingTypesData.forEach(meetingType =>
          queryClient.invalidateQueries({
            queryKey: queryKeyFactory.meetingType(tenantId, meetingType.id),
          })
        )
      }
      await queryClient.invalidateQueries({ queryKey: queryKeyFactory.meetingTypes(workspaceId) })
    },
  })
}

interface ExtendedQueryOptions {
  enabled?: boolean
  retry?: boolean | number | ((failureCount: number, error: { status: number }) => boolean)
  suspense?: boolean
}

export const useMeetingType = (meetingTypeId: string, options?: ExtendedQueryOptions) => {
  const workspaceId = useWorkspaceId()
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()
  return useQuery({
    queryKey: queryKeyFactory.meetingType(tenantId, meetingTypeId),
    queryFn: () => meetingTypes.findMeetingType(tenantId, workspaceId, meetingTypeId),
    enabled: meetingTypeId !== '',
    ...options,
  })
}

export const useMeetingTypes = (userId?: string) => {
  const workspaceId = useWorkspaceId()
  const { meetingTypes, workspace } = useApiClient()
  const { tenantId } = useFireSessionContext()
  return useQuery({
    queryKey: queryKeyFactory.meetingTypes(workspaceId, '', userId),
    queryFn: () => {
      if (!userId) {
        return meetingTypes.findMeetingTypes(tenantId, workspaceId)
      } else {
        return workspace.getPersonal(tenantId, userId).then(personalWorkspace => {
          return meetingTypes.findMeetingTypes(tenantId, personalWorkspace.id)
        })
      }
    },
  })
}

export const usePersonalMeetingTypes = (userId: string) => {
  const { tenantId } = useFireSessionContext()
  const { data: personalWorkspace } = usePersonalWorkspace(userId)

  const { meetingTypes } = useApiClient()

  return useQuery({
    queryKey: queryKeyFactory.personalMeetingTypes(userId),
    queryFn: () => {
      invariant(personalWorkspace, 'personalWorkspace should be defined')
      return meetingTypes.findMeetingTypes(tenantId, personalWorkspace.id) as Promise<
        MeetingTypePersonal[]
      >
    },
    enabled: Boolean(personalWorkspace),
  })
}

export const useDefaultPersonalMeetingType = (userId: string) => {
  const { data: defaultSchedulingLink, isLoading: isDefaultSchedulingLinkLoading } =
    useDefaultSchedulingLinkId(userId)

  const { data: personalMeetingTypes, isLoading: isPersonalMeetingTypesLoading } =
    usePersonalMeetingTypes(userId)

  const meetingTypeId =
    defaultSchedulingLink?.schedulingLink.type === 'PersonalSchedulingLink'
      ? defaultSchedulingLink?.schedulingLink.meetingTypeId
      : null

  return {
    data: personalMeetingTypes?.find(meetingType => meetingType.id === meetingTypeId),
    isLoading: isDefaultSchedulingLinkLoading || isPersonalMeetingTypesLoading,
  }
}

export const useMeetingTypesDrafts = () => {
  const workspaceId = useWorkspaceId()
  const { meetingTypes } = useApiClient()
  const { tenantId } = useFireSessionContext()
  return useQuery({
    queryKey: queryKeyFactory.meetingTypes(workspaceId),
    queryFn: () => meetingTypes.findMeetingTypes(tenantId, workspaceId),
  })
}

export type DraftAndPublishedMeetingType = {
  draft: TypedMeetingTypeDraft
  id: string
  isUnpublishedDraft: boolean
  published?: MeetingType
}

const isGuestFormMixed = (
  guestForm?: GuestFormMixed | GuestFormDataFields
): guestForm is GuestFormMixed => guestForm?.type === 'Mixed'

const mapGuestForm = (guestForm?: GuestFormMixed | GuestFormDataFields) => {
  if (isGuestFormMixed(guestForm)) {
    return guestForm
  }

  const fields = guestForm?.fields.map(field =>
    R.omit(field, ['id'] as Array<never>)
  ) as GuestFormField[]

  return {
    ...guestForm,
    fields,
  }
}

const removeNullishValues = (obj?: object) =>
  obj ? Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null)) : undefined

export const parseForComparison = (
  published?: MeetingType,
  draft?: TypedMeetingTypeDraft['draft']
) => {
  if (!published || !draft) {
    return {}
  }

  const commonKeys = R.intersection(R.keys(published), R.keys(draft))

  const publishedForComparison = R.omit(
    R.pick(published, commonKeys as Array<keyof typeof published>),
    ['metadata'] as Array<never>
  )

  return {
    publishedForComparison: {
      ...publishedForComparison,
      advancedSettings: removeNullishValues(publishedForComparison.advancedSettings),
      reminders: publishedForComparison.reminders?.sort(),
    },
    draftForComparison: R.omit(
      R.pick(
        {
          ...draft,
          reminders: draft?.reminders?.map(reminder => reminder.id).sort(),
          guestForm: mapGuestForm(draft.guestForm),
          advancedSettings: removeNullishValues(draft.advancedSettings),
        },
        commonKeys as Array<keyof typeof draft>
      ),
      ['metadata'] as Array<never>
    ),
  }
}

const getIsUnpublishedDraft = (draft: TypedMeetingTypeDraft, published: MeetingType) => {
  if (draft && !published) {
    return true
  }

  if (draft && published) {
    const { publishedForComparison, draftForComparison } = parseForComparison(
      published,
      draft?.['draft']
    )

    return !isEqualWithDateComparison(publishedForComparison, draftForComparison)
  }

  return false
}

export const parseMeetingTypeData = (
  draftsData: TypedMeetingTypeDraft[],
  publishedData: MeetingType[],
  reminders?: Reminder[]
) => {
  // default availability is created as published and has no draft. we can have a draft without published
  // so we need to merge these ids to get the full list of ids and iterate
  const ids = R.unique([
    ...draftsData.map(draft => draft.draftId),
    ...publishedData.map(published => published.id),
  ])

  const publishedById = R.indexBy(publishedData, x => x.id)
  const draftById = R.indexBy(draftsData, x => x.draftId)
  const data = ids.map(id => {
    const draft = draftById[id]
    const published = publishedById[id]

    return {
      id,
      draft:
        draft ??
        ({
          draftId: id,
          tenantId: published.tenantId,
          workspaceId: published.workspaceId,
          draft: {
            ...R.omit(published, ['metadata', 'workspaceId', 'tenantId']),
            reminders:
              reminders?.filter(reminder => published.reminders?.includes(reminder.id)) ?? [],
          },
          metadata: published.metadata,
        } as TypedMeetingTypeDraft),
      published,
      isUnpublishedDraft: getIsUnpublishedDraft(draft, published),
    }
  }) as DraftAndPublishedMeetingType[]

  return R.sortBy(data, x => {
    return Date.parse((x.draft?.metadata?.updatedAt || x.published?.metadata.updatedAt) ?? '')
  })
}

export const useGetMeetingTypes = (workspaceId: string, name?: string) => {
  const { tenantId } = useFireSessionContext()
  const { meetingTypes: meetingTypesClient } = useApiClient()
  const { data: retrievedReminders } = useReminders()

  const [
    { data: draftsData, isLoading: isLoadingDraft, isError: isErrorDraft },
    { data: publishedData, isLoading: isLoadingPublished, isError: isErrorPublished },
  ] = useQueries({
    queries: [
      queryOptions({
        queryKey: queryKeyFactory.meetingTypesDrafts(workspaceId, name),
        queryFn: () => {
          return meetingTypesClient
            .findMeetingTypeDrafts(tenantId, workspaceId, name ? { name } : undefined)
            .catch(e => {
              if (e.status === 404) {
                return []
              }

              throw e
            }) as Promise<TypedMeetingTypeDraft[]>
        },
        refetchOnWindowFocus: false,
      }),
      queryOptions({
        queryKey: queryKeyFactory.meetingTypes(workspaceId, name),
        queryFn: () => {
          return meetingTypesClient
            .findMeetingTypes(tenantId, workspaceId, name ? { name } : undefined)
            .catch(e => {
              if (e.status === 404) {
                return []
              }

              throw e
            })
        },
        refetchOnWindowFocus: false,
      }),
    ],
  })

  if (!draftsData || !publishedData || isLoadingDraft || isLoadingPublished) {
    return { data: [], isLoading: true, isError: false }
  }

  if (isErrorDraft || isErrorPublished) {
    return { data: [], isLoading: false, isError: true }
  }

  const data = parseMeetingTypeData(draftsData, publishedData, retrievedReminders)

  return { data, isLoading: false, isError: false }
}

export const useFindMeetingTypes = (workspaceId: string | undefined, name?: string) => {
  const { tenantId } = useFireSessionContext()
  const { meetingTypes } = useApiClient()

  return useQuery({
    queryKey: queryKeyFactory.meetingTypes(workspaceId ?? '', name),
    queryFn: async () => {
      return meetingTypes
        .findMeetingTypes(tenantId, workspaceId ?? '', name ? { name } : undefined)
        .catch(e => {
          if (e.status === 404) {
            return []
          }

          throw e
        })
    },
    enabled: Boolean(workspaceId),
    refetchOnWindowFocus: false,
  })
}

// 1. if only one meeting type exists, returns it
// 2. otherwise, returns undefined
// this will be used to autoselect meeting type in display calendar node
// but it should only happen if there is only a single meeting type available
export const getUniqueMeetingType = (meetingTypes?: MeetingType[]) => {
  const teamMeetingTypes = meetingTypes?.filter(meetingType => meetingType.type === 'Team')

  return teamMeetingTypes?.length === 1 ? teamMeetingTypes.at(0) : undefined
}

export const useUniqueMeetingType = () => {
  const { data: meetingTypes, isLoading: isLoading, isError: isError } = useMeetingTypes()
  const uniqueMeetingType = useMemo(() => getUniqueMeetingType(meetingTypes), [meetingTypes])

  if (uniqueMeetingType) {
    return {
      meetingType: uniqueMeetingType,
      isLoading: false,
      isError: false,
    }
  }

  return {
    meetingType: undefined,
    isLoading,
    isError,
  }
}
